import React, { FC, useEffect, useState } from 'react'
import { Loader2Icon } from 'lucide-react'

import { RequiredFieldError } from './RequiredFieldError'
import { RequiredFieldInput } from './RequiredFieldInput'

import FirestoreService from '../../../../../services/firestoreService'

import useToasterHelper from '../../../../../helpers/ToasterHelper'
import {
  hasInvalidRequiredField,
  reqFieldLabel,
  reqFieldValue,
} from '../../../../../helpers/requiredFields'

import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { selectHorses, setHorses } from '../../../../../store/horses/horseSlice'

import { IProfileReqField, IReqFieldType } from './ProfileFields'
import { IHorseData } from '../../../../../models/horse/horse.interface'
import { IOption } from '../../../../../components/inputs/InputNewMemberRow'
import { IUserPublicInterface } from '../../../../../models/users-public/users-public.interface'

import { CONST } from '../../../../../const/const'
import { MESSAGES_CONST } from '../../../../../const/messages-const'

interface TextFieldProps {
  userId?: string
  horseId?: string
  isHorse?: boolean
  onSave: ({ fieldValue }: { fieldValue: string }) => void
  field: IProfileReqField
  editHandler?: (data: IProfileReqField, horse?: IHorseData) => void
  horse?: IHorseData
  teamTabDataWithUser?: IUserPublicInterface[]
  setTeamTabDataWithUser?: (value: IUserPublicInterface[]) => void
}

export const TextField: FC<TextFieldProps> = ({
  field,
  onSave,
  userId,
  isHorse,
  horseId,
  editHandler,
  horse,
  teamTabDataWithUser,
  setTeamTabDataWithUser,
}) => {
  const dispatch = useAppDispatch()

  const myHorses = useAppSelector(selectHorses)

  const [value, setValue] = useState<IOption>({ label: '', value: '' })
  const [loading, setLoading] = useState(false)
  const toastFunctions = useToasterHelper()
  const [type, setType] = useState<IReqFieldType | null>(null)

  const submitHandler = async () => {
    if (!value.value) toastFunctions.error({ message: 'Field is required' })

    setLoading(true)

    const updatedData = {
      [field.fieldKey]: value.value,
    }
    try {
      if (isHorse) {
        await FirestoreService.updateItem(
          CONST.DATA.FIRESTORE.LATEST.COLLECTIONS.HORSES.NAME,
          horseId,
          updatedData
        )
        const updateHorses = myHorses.map((horse) => {
          if (horse.id === horseId) {
            return { ...horse, [field.fieldKey]: value.value }
          } else {
            return horse
          }
        })
        dispatch(setHorses(updateHorses))
      } else {
        await FirestoreService.updateItem(
          CONST.DATA.FIRESTORE.LATEST.COLLECTIONS.USERS.NAME,
          userId,
          updatedData
        )

        const teamTabDataWithUserUpdated =
          teamTabDataWithUser?.map((current) => {
            if (userId === current.id) {
              return { ...current, ...updatedData }
            } else {
              return current
            }
          }) || []
        if (setTeamTabDataWithUser) setTeamTabDataWithUser(teamTabDataWithUserUpdated)
      }

      onSave({ fieldValue: value.value })
      setType(null)
    } catch (error) {
      console.error('error', error)
      return toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    } finally {
      setLoading(false)
    }
  }

  const onChangeInput = (option: IOption) => {
    setValue({ value: option.value, label: option.label })
  }

  useEffect(() => {
    setValue({
      label: reqFieldLabel(field),
      value: reqFieldValue(field),
    })
  }, [field])
  return (
    <>
      <div className="ml-auto flex items-center">
        {type !== 'tel' &&
          type !== 'text' &&
          type !== 'date' &&
          type !== 'select' &&
          !hasInvalidRequiredField(field) && <span>{value.label}</span>}
        {type !== 'tel' && type !== 'text' && type !== 'date' && type !== 'select' && (
          <RequiredFieldError
            setType={setType}
            field={field}
            horse={horse}
            editHandler={editHandler}
          />
        )}
      </div>
      {(type === 'tel' || type === 'text' || type === 'date' || type === 'select') && (
        <div className="ml-auto flex w-full sm:w-auto sm:flex-1 gap-3 sm:gap-5 items-center lg:w-auto relative max-w-full justify-end">
          <div className="flex-1 max-w-[250px] min-w-[100px] w-full sm:w-auto">
            <RequiredFieldInput
              fieldType={field.type}
              fieldKey={field.fieldKey}
              value={value}
              onChange={onChangeInput}
            />
          </div>
          <button disabled={loading} onClick={submitHandler} className="text-[#E80000]">
            {loading ? <Loader2Icon className="animate-spin" /> : 'Save'}
          </button>
        </div>
      )}
    </>
  )
}
